import { useEffect, useMemo, useState } from "react";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calculator, Info } from "lucide-react";

const LEVELS = ["0%", "1%", "2%", "3%", "4%", "5%"];

export default function AgentSimulator() {
  const [plan, setPlan] = useState(null);
  const [bags, setBags] = useState({});
  const [above, setAbove] = useState("");
  const [newCust, setNewCust] = useState("");

  useEffect(() => { api.get("/agent/plan").then((r) => setPlan(r.data)); }, []);

  const res = useMemo(() => {
    if (!plan) return null;
    let base = 0, sales = 0, totalBags = 0;
    const rows = plan.products.map((p) => {
      let c = 0, s = 0, n = 0;
      for (let i = 0; i < 6; i++) {
        const q = Number(bags[`${p.id}-${i}`] || 0);
        c += q * (p.discount_commissions[i] || 0);
        s += q * (p.discount_prices[i] || 0);
        n += q;
      }
      base += c; sales += s; totalBags += n;
      return { id: p.id, name: `${p.name} ${p.pack}`, comm: c, sales: s, n };
    });
    const premium = Number(above || 0);
    const collected = sales + premium;
    const tier = plan.accelerator_tiers.find((t) => collected >= t.min && (t.max == null || collected <= t.max)) || plan.accelerator_tiers[0];
    const accel = base * tier.boost;
    const aboveBonus = premium * plan.above_split;
    const nc = Number(newCust || 0) * plan.new_customer_bonus;
    return { rows, base, sales, collected, totalBags, tier, accel, aboveBonus, nc, total: base + accel + aboveBonus + nc };
  }, [plan, bags, above, newCust]);

  if (!plan || !res) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Calculator className="w-6 h-6 text-primary" /> Earnings simulator</h1>
        <p className="text-sm text-muted-foreground">What-if only - enter paid bags per discount level. Nothing here is saved or sent to the company.</p>
      </div>

      <Card className="p-4 overflow-x-auto" data-testid="simulator-grid">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground uppercase tracking-wider">
              <th className="text-left py-1.5 pr-2">Product</th>
              {LEVELS.map((l) => <th key={l} className="py-1.5 text-center">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {plan.products.map((p) => (
              <tr key={p.id} className="border-t border-border/40">
                <td className="py-1.5 pr-2 whitespace-nowrap">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-muted-foreground">{p.pack} · {p.discount_commissions[0]} AED/bag</p>
                </td>
                {LEVELS.map((_, i) => (
                  <td key={i} className="py-1.5 px-0.5">
                    <Input type="number" min="0" inputMode="numeric" className="h-9 w-12 px-1 text-center text-sm" value={bags[`${p.id}-${i}`] || ""} placeholder="0"
                      onChange={(e) => setBags({ ...bags, [`${p.id}-${i}`]: e.target.value })} data-testid={`sim-${p.id}-${i}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">Collected ABOVE list price (AED)</p>
          <Input type="number" min="0" value={above} placeholder="0" onChange={(e) => setAbove(e.target.value)} data-testid="sim-above-input" />
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted-foreground mb-1">New customers (≥ {plan.new_customer_threshold} AED first order)</p>
          <Input type="number" min="0" value={newCust} placeholder="0" onChange={(e) => setNewCust(e.target.value)} data-testid="sim-newcust-input" />
        </Card>
      </div>

      <Card className="p-5 space-y-2 text-sm" data-testid="simulator-result">
        <Row l={`Bags sold (${res.totalBags})`} v={res.sales} />
        <Row l="Total collected sales" v={res.collected} />
        <Row l="Base commission" v={res.base} strong />
        <div className="flex justify-between items-center py-1 border-b border-border/40">
          <span className="text-muted-foreground">Accelerator tier</span>
          <Badge variant={res.tier.boost ? "default" : "secondary"}>{res.tier.label}</Badge>
        </div>
        <Row l={`Accelerator bonus (base × ${Math.round(res.tier.boost * 100)}%)`} v={res.accel} />
        <Row l={`Above-price bonus (${Math.round(plan.above_split * 100)}% of premium)`} v={res.aboveBonus} />
        <Row l={`New-customer bonus (× ${plan.new_customer_bonus} AED)`} v={res.nc} />
        <div className="flex justify-between pt-3 mt-1 border-t-2 border-foreground">
          <span className="font-semibold">TOTAL MONTHLY EARNINGS</span>
          <span className="font-display text-2xl font-extrabold text-primary" data-testid="sim-total">{money(res.total)}</span>
        </div>
      </Card>

      <Card className="p-4 bg-secondary/40 flex items-start gap-3">
        <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Uses today's live price list and policy. Star Agent bonus ({plan.star_agent_bonus} AED) is awarded to the month's top seller and is not included here.
          Put all of a product's bags in the 0% column if everything sold at full price.
        </p>
      </Card>
    </div>
  );
}

function Row({ l, v, strong }) {
  return (
    <div className={`flex justify-between py-1 border-b border-border/40 ${strong ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{l}</span><span>{money(v)}</span>
    </div>
  );
}
